import { useState } from "react";
import { Plus, Clock } from "lucide-react";
import { Visualizer } from "./Visualizer";
import { formatDuration } from "../utils";
import { STREAMS, type StreamId } from "../constants";
import type { Track, PushResult } from "../types";

interface TrackItemProps {
  track: Track;
  streamId: StreamId;
  albumFolder?: string;
  isCurrentTrack: boolean;
  isPlaying: boolean;
  color: string;
  showQueueButton?: boolean;
  onQueue?: (albumFolder: string, trackFilename: string) => Promise<PushResult>;
}

export function TrackItem({
  track,
  streamId,
  albumFolder,
  isCurrentTrack,
  isPlaying,
  color,
  showQueueButton,
  onQueue,
}: TrackItemProps) {
  const [queuing, setQueuing] = useState(false);
  const [queueResult, setQueueResult] = useState<"success" | "error" | null>(null);

  const trackImage = track.images.find(img => img !== "album-art.jpg") || track.images[0];
  const imageUrl = trackImage ? `/api/artwork/${albumFolder || streamId}/${trackImage}` : STREAMS[streamId]?.artwork;
  const fallbackImage = STREAMS[streamId]?.fallbackImage || '/swansong.city.jpg';

  const handleQueue = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!albumFolder || !onQueue || queuing || queueResult) return;

    setQueuing(true);
    const result = await onQueue(albumFolder, track.filename);
    setQueuing(false);
    setQueueResult(result.success ? "success" : "error");
    setTimeout(() => setQueueResult(null), 3000);
  };

  return (
    <div
      className={`flex items-center gap-4 p-3 rounded-lg transition-all ${
        isCurrentTrack
          ? "bg-[var(--bg-elevated)] border border-[var(--border-subtle)]"
          : "hover:bg-[var(--bg-surface)]"
      }`}
      style={{
        borderColor: isCurrentTrack ? color : undefined,
        boxShadow: isCurrentTrack ? `0 0 20px ${color}20` : undefined,
      }}
    >
      {showQueueButton && (
        <button
          onClick={handleQueue}
          disabled={queuing || queueResult !== null}
          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
            queueResult === "success"
              ? "bg-green-500/20 text-green-500"
              : queueResult === "error"
              ? "bg-red-500/20 text-red-500"
              : queuing
              ? "bg-[var(--border-subtle)] text-[var(--text-muted)]"
              : "bg-[var(--border-subtle)] hover:bg-[var(--border-muted)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          }`}
        >
          {queueResult === "success" ? (
            <span className="text-xs">✓</span>
          ) : queueResult === "error" ? (
            <span className="text-xs">✗</span>
          ) : queuing ? (
            <span className="text-xs animate-pulse">...</span>
          ) : (
            <Plus size={16} />
          )}
        </button>
      )}

      <div className="w-12 h-12 md:w-14 md:h-14 rounded overflow-hidden flex-shrink-0 relative">
        <img
          src={imageUrl}
          alt={track.title}
          loading="eager"
          onError={(e) => {
            const img = e.target as HTMLImageElement;
            if (!img.src.endsWith(fallbackImage)) {
              img.src = fallbackImage;
            }
          }}
          className="w-full h-full object-cover"
        />
        {isCurrentTrack && isPlaying && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Visualizer color={color} active={true} />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="font-mono text-xs"
            style={{ color: isCurrentTrack ? color : "var(--text-muted)" }}
          >
            {track.track.toString().padStart(2, "0")}
          </span>
          <h4
            className={`font-medium truncate ${isCurrentTrack ? "" : "text-[var(--text-primary)]"}`}
            style={{ color: isCurrentTrack ? color : undefined }}
          >
            {track.title}
          </h4>
        </div>
        <p className="text-sm text-[var(--text-secondary)] truncate">{track.artist}</p>
      </div>

      <div className="flex items-center gap-2 text-[var(--text-muted)] w-20 justify-end flex-shrink-0">
        {isCurrentTrack && (
          <div
            className="w-2 h-2 rounded-full animate-pulse flex-shrink-0"
            style={{ backgroundColor: color }}
          />
        )}
        <Clock size={14} className="flex-shrink-0" />
        <span className="font-mono text-sm">{formatDuration(track.duration)}</span>
      </div>
    </div>
  );
}
